import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Badge, Button, toast } from '@wit-support/ui';
import { RailCard, Time, formatInZone, useAdminTimezone } from '@wit-support/ui-domain';
import { Clock } from 'lucide-react';
import { listScheduledEmails, cancelScheduledEmail, ConversationMergedError } from '../api.js';
// ---------------------------------------------------------------------------
// Countdown helper
// ---------------------------------------------------------------------------
/**
 * Returns a live «через X» or «X назад» label for `scheduledFor`, ticking every
 * second while the item is still pending. Falls back to an absolute timestamp
 * once the value is more than 4 days out.
 */
const LIVE_WINDOW_MS = 4 * 86_400_000;
function useCountdown(scheduledFor) {
    const zone = useAdminTimezone();
    const [now, setNow] = useState(() => Date.now());
    // Only tick for a FUTURE target within the live-countdown window. For dates
    // >4 days out the label is a fixed absolute timestamp that never changes, so
    // a per-second interval would re-render for no visible change. For PAST-DUE
    // rows the «X назад» label likewise doesn't need to keep re-rendering every
    // second forever — render it static (no interval). This is INTENTIONAL: the
    // panel's ~10s poll (refetch while any row is non-terminal) re-renders these
    // rows; the overdue label is computed from `Date.now()` read DIRECTLY at
    // render time (NOT the frozen `now` state), so each poll-driven re-render
    // reflects real elapsed time — it lags at most ~10s, acceptable and far
    // cheaper than a per-second interval per past-due row. Inside the future
    // window we tick the `now` state every second.
    // Note: a >4-day item that crosses INTO the window mid-session goes live on
    // the next external re-render — the panel's 10s poll (refetch while any row is
    // non-terminal) re-renders pending rows and self-heals it, so no one-shot
    // boundary timeout is needed here.
    const t = new Date(scheduledFor).getTime();
    const live = !Number.isNaN(t) && t > now && t - now < LIVE_WINDOW_MS;
    useEffect(() => {
        if (!live)
            return;
        const id = setInterval(() => setNow(Date.now()), 1_000);
        return () => clearInterval(id);
    }, [live]);
    if (Number.isNaN(t))
        return scheduledFor;
    // Live (future, in-window) branch reads the ticking `now` state; the non-live
    // branch (past-due / far-future) reads Date.now() FRESH at render so a poll-
    // driven re-render refreshes the «X назад» label instead of using the frozen
    // mount-time `now`.
    const ref = live ? now : Date.now();
    const diffMs = t - ref;
    const abs = Math.abs(diffMs);
    const sec = Math.round(abs / 1000);
    const past = diffMs < 0;
    if (abs < 60_000) {
        const s = Math.max(0, sec);
        return past ? `${s} с назад` : `через ${s} с`;
    }
    if (abs < 3600_000) {
        const m = Math.round(abs / 60_000);
        return past ? `${m} мин назад` : `через ${m} мин`;
    }
    if (abs < 4 * 86_400_000) {
        const h = Math.round(abs / 3600_000);
        return past ? `${h} ч назад` : `через ${h} ч`;
    }
    // >4 days — absolute date
    return formatInZone(scheduledFor, 'dd.MM.yyyy, HH:mm', zone);
}
// ---------------------------------------------------------------------------
// Origin badge
// ---------------------------------------------------------------------------
const SOURCE_LABEL = {
    admin: 'Админка',
    telegram: 'Telegram',
    pipeline: 'Пайплайн',
};
const SOURCE_VARIANT = {
    admin: 'neutral',
    telegram: 'info',
    pipeline: 'accent',
};
function OriginBadge({ item }) {
    const label = item.source === 'pipeline' && item.pipelineName
        ? `Пайплайн «${item.pipelineName}»`
        : SOURCE_LABEL[item.source];
    return (_jsx(Badge, { variant: SOURCE_VARIANT[item.source], size: "sm", children: label }));
}
// ---------------------------------------------------------------------------
// Pending row
// ---------------------------------------------------------------------------
// Keyed by the contract enum so a new cancel reason fails typecheck here
// instead of leaking a raw English token into the history row.
const CANCEL_REASON_RU = {
    manual: 'отменено вручную',
    blacklist: 'отправитель в ЧС',
    duplicate_template: 'дубликат шаблона',
    merge_duplicate: 'дубликат (слияние)',
};
const STATUS_RU = {
    pending: 'Ожидает',
    enqueued: 'В очереди',
    sent: 'Отправлено',
    cancelled: 'Отменено',
    failed: 'Ошибка',
};
const STATUS_VARIANT = {
    pending: 'info',
    enqueued: 'info',
    sent: 'success',
    cancelled: 'neutral',
    failed: 'danger',
};
function PendingRow({ item, conversationId, canReply, }) {
    const countdown = useCountdown(item.scheduledFor);
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [confirming, setConfirming] = useState(false);
    const cancelMut = useMutation({
        mutationFn: () => cancelScheduledEmail(conversationId, item.id),
        onSuccess: () => {
            toast.success('Отложенное письмо отменено');
            void queryClient.invalidateQueries({ queryKey: ['scheduled-emails', conversationId] });
            // Refresh the inbox list so a just-cleared conversation drops out of the
            // «С отложенными письмами» (hasScheduledEmail) filter immediately rather
            // than lingering up to 10s until the next poll.
            void queryClient.invalidateQueries({ queryKey: ['conversations'] });
            setConfirming(false);
        },
        onError: (err) => {
            if (err instanceof ConversationMergedError) {
                // The conversation was tombstoned + merged — navigate to the surviving
                // target rather than reporting a misleading cancel failure (mirrors the
                // reply composer's merge handling). The DELETE never reached a real row.
                // Do NOT reset confirming — navigate() unmounts this component.
                if (err.targetId) {
                    toast.success('Redirected — this conversation was merged');
                    void navigate({
                        to: '/conversations/$conversationId',
                        params: { conversationId: err.targetId },
                    });
                    return;
                }
                // Target unreadable (malformed 303 / cross-origin) — can't navigate;
                // prompt a refresh and let the component recover.
                toast.error('Эта переписка была объединена — обновите страницу');
                setConfirming(false);
                return;
            }
            toast.error(err.message);
            setConfirming(false);
        },
    });
    const label = item.templateName ?? item.subject ?? '(без темы)';
    return (_jsxs("div", { "data-testid": "pending-row", className: "flex flex-col gap-1.5 rounded-md border border-[var(--color-border-default)] bg-[var(--color-surface-default)] px-2.5 py-2", children: [_jsxs("div", { className: "flex items-start justify-between gap-2", children: [_jsx("span", { className: "min-w-0 truncate text-sm text-[var(--color-text-primary)]", children: label }), _jsx(OriginBadge, { item: item })] }), _jsxs("div", { className: "flex items-center justify-between gap-2", children: [_jsxs("span", { className: "flex items-center gap-1 text-xs text-[var(--color-text-muted)]", children: [_jsx(Clock, { className: "h-3 w-3 shrink-0", "aria-hidden": "true" }), countdown] }), canReply &&
                        (confirming ? (_jsxs("div", { className: "flex items-center gap-1", children: [_jsx("span", { className: "text-xs text-[var(--color-text-muted)]", children: "\u041E\u0442\u043C\u0435\u043D\u0438\u0442\u044C?" }), _jsx(Button, { type: "button", variant: "ghost", size: "sm", className: "text-[var(--color-state-danger-fg)]", disabled: cancelMut.isPending, onClick: () => cancelMut.mutate(), "data-testid": "confirm-cancel", children: "\u0414\u0430" }), _jsx(Button, { type: "button", variant: "ghost", size: "sm", disabled: cancelMut.isPending, onClick: () => setConfirming(false), children: "\u041D\u0435\u0442" })] })) : (_jsx(Button, { type: "button", variant: "ghost", size: "sm", onClick: () => setConfirming(true), disabled: cancelMut.isPending, "data-testid": "cancel-btn", children: "\u041E\u0442\u043C\u0435\u043D\u0438\u0442\u044C" })))] })] }));
}
// ---------------------------------------------------------------------------
// History row
// ---------------------------------------------------------------------------
function HistoryRow({ item }) {
    const label = item.templateName ?? item.subject ?? '(без темы)';
    return (_jsxs("div", { "data-testid": "history-row", className: "flex flex-col gap-1 rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-surface-canvas)] px-2.5 py-2 opacity-70", children: [_jsxs("div", { className: "flex items-center justify-between gap-2", children: [_jsx("span", { className: "min-w-0 truncate text-xs text-[var(--color-text-secondary)]", children: label }), _jsx(Badge, { variant: STATUS_VARIANT[item.status], size: "sm", children: STATUS_RU[item.status] })] }), _jsxs("div", { className: "flex items-center justify-between gap-2 text-xs text-[var(--color-text-muted)]", children: [_jsx(Time, { iso: item.sentAt ?? item.createdAt, preset: "shortNumeric" }), item.cancelReason ? (_jsx("span", { children: CANCEL_REASON_RU[item.cancelReason] ?? item.cancelReason })) : null, _jsx(OriginBadge, { item: item })] })] }));
}
// ---------------------------------------------------------------------------
// Queue panel
// ---------------------------------------------------------------------------
const TERMINAL = new Set(['sent', 'cancelled', 'failed']);
export function ScheduledQueuePanel({ conversationId, canReply, }) {
    const query = useQuery({
        queryKey: ['scheduled-emails', conversationId],
        queryFn: () => listScheduledEmails(conversationId),
        // Poll while there are active rows so the status flips in without a refresh.
        // INTENTIONAL bounded background polling: react-query issues at most one
        // in-flight request at a time (the next interval only starts after the
        // previous response lands), so this is not a resource leak. If the worker
        // is down and a row is stuck in a non-terminal state (pending/enqueued),
        // the panel will keep polling every 10 s for the lifetime of this open
        // browser thread — acceptable UX trade-off; do NOT add a stop-polling bound
        // here, as that would leave a legitimately-processing queue appearing stale.
        refetchInterval: (q) => (q.state.data ?? []).some((r) => !TERMINAL.has(r.status)) ? 10_000 : false,
    });
    // While the first load is in flight (no data yet), render nothing.
    if (query.isLoading && !query.data)
        return null;
    // Fail-soft on a persistent error with NO last-good data: show a small inline
    // notice rather than silently removing the whole «Отложенные письма» block.
    // react-query retains `data` across a background-refetch error, so when prior
    // data exists we fall through and keep showing it (the panel never blinks out).
    if (!query.data) {
        if (query.isError) {
            return (_jsx(RailCard, { icon: Clock, title: "\u041E\u0442\u043B\u043E\u0436\u0435\u043D\u043D\u044B\u0435 \u043F\u0438\u0441\u044C\u043C\u0430", children: _jsx("p", { role: "alert", className: "rounded-[var(--radius-2)] bg-[var(--color-surface-sunken)] px-3 py-1.5 text-xs text-[var(--color-text-muted)]", children: "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0437\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C \u043E\u0442\u043B\u043E\u0436\u0435\u043D\u043D\u044B\u0435 \u043F\u0438\u0441\u044C\u043C\u0430" }) }));
        }
        return null;
    }
    const pending = query.data.filter((r) => !TERMINAL.has(r.status));
    const history = query.data.filter((r) => TERMINAL.has(r.status));
    if (pending.length === 0 && history.length === 0) {
        return (_jsx(RailCard, { icon: Clock, title: "\u041E\u0442\u043B\u043E\u0436\u0435\u043D\u043D\u044B\u0435 \u043F\u0438\u0441\u044C\u043C\u0430", children: _jsx("p", { className: "text-xs text-[var(--color-text-muted)]", children: "\u041D\u0435\u0442 \u0437\u0430\u043F\u043B\u0430\u043D\u0438\u0440\u043E\u0432\u0430\u043D\u043D\u044B\u0445 \u043F\u0438\u0441\u0435\u043C" }) }));
    }
    return (_jsxs(RailCard, { icon: Clock, title: "\u041E\u0442\u043B\u043E\u0436\u0435\u043D\u043D\u044B\u0435 \u043F\u0438\u0441\u044C\u043C\u0430", headerExtra: query.isError ? (
        // A background poll failed AFTER a first successful load — keep showing
        // the (now possibly stale) rows but flag that they may be out of date.
        _jsx("span", { role: "status", "data-testid": "stale-indicator", className: "ml-auto text-[10px] font-normal normal-case text-[var(--color-text-muted)] opacity-70", children: "\u043E\u0431\u043D\u043E\u0432\u043B\u0435\u043D\u0438\u0435 \u043D\u0435\u0434\u043E\u0441\u0442\u0443\u043F\u043D\u043E" })) : null, children: [pending.length > 0 ? (_jsx("div", { className: "flex flex-col gap-1.5", children: pending.map((item) => (_jsx(PendingRow, { item: item, conversationId: conversationId, canReply: canReply }, item.id))) })) : null, history.length > 0 ? (_jsxs(_Fragment, { children: [pending.length > 0 ? (_jsx("div", { className: "border-t border-[var(--color-border-subtle)]" })) : null, _jsx("span", { className: "text-xs font-medium text-[var(--color-text-muted)]", children: "\u0418\u0441\u0442\u043E\u0440\u0438\u044F" }), _jsx("div", { className: "flex flex-col gap-1.5", children: history.map((item) => (_jsx(HistoryRow, { item: item }, item.id))) })] })) : null] }));
}
//# sourceMappingURL=queue-panel.js.map